import axios from 'axios';

const paypal = {
  createOrder: async ({ plan, price, auth0Id, token, user }) => {
    if (!plan || !price || !auth0Id) {
      throw new Error('Missing required parameters: plan, price, or auth0Id');
    }

    // Use explicit backend URL with fallback
    const apiUrl = process.env.REACT_APP_API_URL || 'https://intellegacy-back.vercel.app';
    // Remove trailing slash to prevent double slash in URL
    const cleanApiUrl = apiUrl.replace(/\/$/, '');
    
    try {
      const response = await axios.post(`${cleanApiUrl}/api/paypal/create-order`, {
        plan,
        price,
        auth0Id
      }, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          // Add user headers for JWE token processing
          'x-user-sub': user?.sub || '',
          'x-user-email': user?.email || '',
          'x-user-name': user?.name || '',
          'x-user-given-name': user?.given_name || '',
          'x-user-family-name': user?.family_name || ''
        },
        timeout: 30000, // 30 second timeout
        // Add explicit error handling for network issues
        validateStatus: function (status) {
          return status < 500; // Resolve only if status is less than 500
        }
      });

      if (response.status >= 400) {
        throw new Error(response.data?.error || `Server error: ${response.status}`);
      }

      if (!response.data.success) {
        throw new Error(response.data.error || 'Failed to create order');
      }

      // Return in the format expected by RequestAccessModal
      return { data: { id: response.data.orderId } };
    } catch (error) {
      // Handle different types of errors
      if (error.code === 'ECONNABORTED' || error.message.includes('timeout')) {
        throw new Error('Request timeout - please check your connection and try again');
      } else if (error.code === 'NETWORK_ERROR' || !error.response) {
        throw new Error('Network error - unable to reach payment server');
      } else if (error.response) {
        throw new Error(error.response.data?.error || error.message || 'Payment server error');
      } else {
        throw new Error(error.message || 'Failed to create order');
      }
    }
  },

  captureOrder: async ({ orderId, auth0Id, token, user }) => {
    if (!orderId || !auth0Id) {
      throw new Error('Missing required parameters: orderId or auth0Id');
    }

    // Use explicit backend URL with fallback
    const apiUrl = process.env.REACT_APP_API_URL || 'https://intellegacy-back.vercel.app';
    // Remove trailing slash to prevent double slash in URL
    const cleanApiUrl = apiUrl.replace(/\/$/, '');
    
    try {
      const response = await axios.post(`${cleanApiUrl}/api/paypal/capture-order`, {
        orderId,
        auth0Id
      }, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          // Add user headers for JWE token processing
          'x-user-sub': user?.sub || '',
          'x-user-email': user?.email || '',
          'x-user-name': user?.name || '',
          'x-user-given-name': user?.given_name || '',
          'x-user-family-name': user?.family_name || ''
        },
        timeout: 30000, // 30 second timeout
        validateStatus: function (status) {
          return status < 500; // Resolve only if status is less than 500
        }
      });

      if (response.status >= 400) {
        throw new Error(response.data?.error || `Server error: ${response.status}`);
      }

      if (!response.data.success) {
        throw new Error(response.data.error || 'Failed to capture payment');
      }

      return response.data.captureId;
    } catch (error) {
      // Handle different types of errors
      if (error.code === 'ECONNABORTED' || error.message.includes('timeout')) {
        throw new Error('Request timeout - please check your connection and try again');
      } else if (error.code === 'NETWORK_ERROR' || !error.response) {
        throw new Error('Network error - unable to reach payment server');
      } else if (error.response) {
        throw new Error(error.response.data?.error || error.message || 'Payment server error');
      } else {
        throw new Error(error.message || 'Failed to capture payment');
      }
    }
  }
};

// Working implementation for createAccessRequest
export async function createAccessRequest({ auth0Id, token, user, reason }) {
  if (!auth0Id || !token) throw new Error('Missing auth0Id or token');
  
  // Use explicit backend URL with fallback
  const apiUrl = process.env.REACT_APP_API_URL || 'https://intellegacy-back.vercel.app';
  // Remove trailing slash to prevent double slash in URL
  const cleanApiUrl = apiUrl.replace(/\/$/, '');
  
  try {
    const response = await axios.post(`${cleanApiUrl}/api/access-request`, { 
      auth0Id, 
      reason: reason || '' 
    }, {
      headers: { 
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
        // Add user headers for JWE token processing
        'x-user-sub': user?.sub || '',
        'x-user-email': user?.email || '',
        'x-user-name': user?.name || '',
        'x-user-given-name': user?.given_name || '',
        'x-user-family-name': user?.family_name || ''
      },
      timeout: 30000,
      validateStatus: function (status) {
        return status < 500;
      }
    });
    
    if (response.status >= 400) {
      throw new Error(response.data?.error || `Server error: ${response.status}`);
    }
    
    return response.data;
  } catch (error) {
    if (error.code === 'ECONNABORTED' || error.message.includes('timeout')) {
      throw new Error('Request timeout - please check your connection');
    } else if (error.code === 'NETWORK_ERROR' || !error.response) {
      throw new Error('Network error - unable to reach server');
    } else if (error.response) {
      throw new Error(error.response.data?.error || error.message || 'Server error');
    } else {
      throw new Error(error.message || 'Failed to create access request');
    }
  }
}

export default paypal;